import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../../config';
import { BookOpen, FolderOpen, List, Clock, BarChart3 } from 'lucide-react';

const apiUrl = config.apiUrl;

const ViewModule = ({ moduleId }) => {
  const [module, setModule] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (moduleId) {
      fetchModuleDetails();
    }
  }, [moduleId]);

  const fetchModuleDetails = async () => {
    try {
      setLoading(true);
      const moduleResponse = await axios.get(`${apiUrl}/modules/${moduleId}`);
      setModule(moduleResponse.data);
      
      const lessonsResponse = await axios.get(`${apiUrl}/modules/${moduleId}/lessons`);
      setLessons(lessonsResponse.data);
    } catch (err) {
      setError('Failed to load module details');
      console.error('Error fetching module details:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalRevisionTime = () => {
    const revisionStages = [24, 72, 168, 360, 720]; // hours for each revision stage
    return lessons.reduce((total, lesson) => {
      const stageIndex = Math.min(lesson.revision_number || 0, revisionStages.length - 1);
      return total + revisionStages[stageIndex];
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg">Loading module details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-yellow-100 text-yellow-700 p-4 rounded-lg">
          Module not found
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6">
          <div className="flex items-center space-x-4 mb-4">
            <FolderOpen size={32} />
            <div>
              <h1 className="text-2xl font-bold">{module.name}</h1>
              <p className="text-blue-100">{module.subject?.name}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{lessons.length}</div>
              <div className="text-sm">Lessons</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{module.order}</div>
              <div className="text-sm">Order</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{calculateTotalRevisionTime()}</div>
              <div className="text-sm">Total Revision Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">
                {lessons.filter(l => l.revision_number > 0).length}
              </div>
              <div className="text-sm">Revised Lessons</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('lessons')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'lessons'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Lessons ({lessons.length})
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {module.description && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-gray-600">{module.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <BarChart3 size={18} className="mr-2" />
                    Progress Overview
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Total Lessons</span>
                      <span className="font-semibold">{lessons.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Completed Revisions</span>
                      <span className="font-semibold">
                        {lessons.filter(l => l.revision_number > 0).length}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pending Revisions</span>
                      <span className="font-semibold">
                        {lessons.filter(l => l.revision_number === 0).length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Clock size={18} className="mr-2" />
                    Time Commitment
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Estimated Revision Time</span>
                      <span className="font-semibold">{calculateTotalRevisionTime()} hours</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Average per Lesson</span>
                      <span className="font-semibold">
                        {lessons.length > 0 ? Math.round(calculateTotalRevisionTime() / lessons.length) : 0} hours
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'lessons' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Lessons in this Module</h3>
              
              {lessons.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <BookOpen className="mx-auto mb-4 text-gray-400" size={48} />
                  <p>No lessons found in this module</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {lessons.map(lesson => (
                    <div key={lesson.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-800">{lesson.title}</h4>
                          <p className="text-sm text-gray-600">Order: {lesson.order}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            lesson.revision_number > 0 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {lesson.revision_number > 0 ? `Revised ${lesson.revision_number}x` : 'Needs Revision'}
                          </span>
                        </div>
                      </div>
                      
                      {lesson.content && (
                        <p className="text-gray-600 text-sm mt-2 line-clamp-2">
                          {lesson.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'details' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2">Module Information</h3>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="font-medium text-gray-500">Module Name</dt>
                    <dd className="text-gray-900">{module.name}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">Subject</dt>
                    <dd className="text-gray-900">{module.subject?.name}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">Order</dt>
                    <dd className="text-gray-900">{module.order}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">Created</dt>
                    <dd className="text-gray-900">
                      {new Date(module.created_at).toLocaleDateString()}
                    </dd>
                  </div>
                </dl>
              </div>

              {module.description && (
                <div>
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-gray-600">{module.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewModule;